import {
  classNamesFunction,
  FontSizes,
  ComboBox,
  IComboBoxOption,
  IComboBox,
  FontWeights,
  PrimaryButton,
  Label,
  IComboBoxStyles,
  Modal,
  DefaultButton,
  Button,
  IBreadcrumbItem,
} from "@fluentui/react";
import { Stack, IStackTokens, StackItem } from "@fluentui/react/lib/Stack";
import {
  Breadcrumb,
  Card,
  CardStandardHeader,
} from "@coherence-design-system/controls";
import React, { useEffect, useState } from "react";
import {
  CountryProfileProps,
  CountryProfileStyleProps,
  CountryProfileStyles,
  IRoles,
  IUserProvisioningResponse,
} from "./CountryProfile.types";
import "./CountryProfileStyle.css";
import { UserCategories, WORLD_WIDE } from "./Common/CountryProfileData";
import { UserProvisiongService } from "../../services/UserProvioning/UserProvioningService";
import { appInsights } from "../../services/Logging/AppInsights";
import { useLocalStorageContext } from "../../Provider/LocalStorageProvider";
import { BreadcrumbStyle } from "../Quotation/AddQuotation/AddQuotationStyle";
import { useNavigate } from "react-router";

const getClassNames = classNamesFunction<
  CountryProfileStyleProps,
  CountryProfileStyles
>();

export const comboBoxStyles: Partial<IComboBoxStyles> = {
  root: {
    width: 290,
  },
};

export const CountryProfileBase: React.FunctionComponent<
  CountryProfileProps
> = (props: CountryProfileProps) => {
  const { styles, theme } = props;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const classNames = getClassNames(styles, {
    theme: theme!,
  });
  const [selectedUser, setSelectedUser] = useState<IComboBoxOption>();
  const [selectedRole, setSelectedRole] = useState<IRoles | null>();
  const [rolesData, setRolesData] = useState<IRoles[] | []>([]);
  const [userProvisioningData, setUserProvisioningData] =
    useState<IUserProvisioningResponse>();

  const [isModalOpen, setIsModalOpen] = useState(true);

  const [isCloseButtonClicked, setIsCloseButtonclicked] = useState(false);

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setIsCloseButtonclicked(true);
  };

  const modalStyles = {
    main: {
      width: "400px", // Set the width of the modal
      height: "200px", // Set the height of the modal
    },
  };

  const getAccessConfiguration = async () => {
    appInsights.trackEvent({
      name: "In CountryProfile.base.tsx, calling get access configurations endpoint.",
    });
    UserProvisiongService.getAccessConfiguration()
      .then((res) => {
        if (res?.data) {
          appInsights.trackEvent({
            name: "In CountryProfile.base.tsx, access configurations fetched successfully.",
          });
          setUserProvisioningData(res.data);
        } else {
          appInsights.trackEvent({
            name: "In CountryProfile.base.tsx, access configurations have no data.",
          });
          setUserProvisioningData(undefined);
        }
      })
      .catch((error: any) => {
        appInsights.trackException({
          error,
          properties: {
            name: "In CountryProfile.base.tsx, error while getting access configurations for user provisioning.",
          },
        });
        console.log("ERROR in access configuration endpoint", error);
        setUserProvisioningData(undefined);
      });
  };
  useEffect(() => {
    if (userProvisioningData) {
      // let data: IUserProvisioningResponse = userProvisioningData;
      const regionsData =
        userProvisioningData?.regions?.filter(
          (item) => item.name.toLocaleLowerCase() !== "china"
        ) ?? [];
      if (selectedUser?.key! === UserCategories[0].key) {
        const formattedData = regionsData?.map((item) => {
          return {
            key: item.advocateGroupId,
            text: item.name,
            requestUrl: item.advocateMyAccessLink,
          };
        });
        setRolesData(formattedData);
      } else if (selectedUser?.key! === UserCategories[1].key) {
        const formattedData = regionsData?.map((item) => {
          return {
            key: item.managerGroupId,
            text: item.name,
            requestUrl: item.manageMyAccessLink,
          };
        });
        setRolesData(formattedData);
      } else if (selectedUser?.key! === UserCategories[2].key) {
        setRolesData([
          {
            key: userProvisioningData.gtfsGroupId,
            text: WORLD_WIDE,
            requestUrl: userProvisioningData.gtfsMyAccessLink,
          },
        ]);
      } else if (selectedUser?.key! === UserCategories[3].key) {
        // CENOPS role - has access to all regions with full edit and create permissions.
        setRolesData([
          {
            key: userProvisioningData.cenopsGroupId,
            text: WORLD_WIDE,
            requestUrl: userProvisioningData.cenopsMyAccessLink,
          },
        ]);
      }
      // else if (selectedUser?.key! === UserCategories[3].key) {
      //   setRolesData([
      //     {
      //       key: data.wwcdGroupId,
      //       text: selectedUser?.key! as string,
      //       requestUrl: data.wwcdMyAccessLink,
      //     },
      //   ]);
      // }
    }
  }, [selectedUser?.key]);

  useEffect(() => {
    getAccessConfiguration();
  }, []);

  const onUserChange = (
    event: React.FormEvent<IComboBox>,
    option?: IComboBoxOption | undefined,
    index?: number | undefined,
    value?: string | undefined
  ) => {
    if (option) {
      setSelectedUser(option);
      setSelectedRole(null);
    }
  };

  const onRoleChange = (
    event: React.FormEvent<IComboBox>,
    option?: IComboBoxOption | undefined,
    index?: number | undefined,
    value?: string | undefined
  ) => {
    if (option) {
      setSelectedRole(option as IRoles);
    }
  };

  const stackTokensWithGap10: Partial<IStackTokens> = { childrenGap: 10 };
  const { isUserSignedIn, isUserAuthorized } = useLocalStorageContext();
  const navigate = useNavigate();

  if (!isUserAuthorized && !isCloseButtonClicked) {
    return (
      <Stack>
        <Modal
          isOpen={isModalOpen}
          styles={modalStyles}
          containerClassName="customModalContainer" // Add a custom class if needed
        >
          <Stack style={{ padding: "10px" }}>
            <p>
              <a
                href="https://go.microsoft.com/fwlink/?LinkId=521839"
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: "none" }}
              >
                {" "}
                <h2 style={{ color: "rgb(0, 90, 158)" }}>Privacy Statement</h2>
              </a>
              <p>Please read the privacy policy carefully</p>
            </p>
            <Button
              style={{
                marginLeft: "280px",
                marginTop: "28px",
                backgroundColor: "transperent",
                color: "black",
                width: "20px",
              }}
              onClick={closeModal}
            >
              Close
            </Button>
          </Stack>
        </Modal>
      </Stack>
    );
  }

  const getBreadcrumbItems = () => {
    const items: IBreadcrumbItem[] = [
      {
        text: "Access Request",
        key: "Access Request",
        onClick: () => {
          navigate("/AccessRequest");
          window.location.reload();
        },
      },
      {
        text: "User Role Request",
        key: "User Role Request",
      },
    ];
    return items;
  };

  return (
    <Stack
      tokens={stackTokensWithGap10}
      style={{
        backgroundColor:
          isUserSignedIn && !isUserAuthorized ? "#FFFFFF" : "#FAF9F8",
      }}
    >
      {isUserSignedIn && isUserAuthorized && (
        <Breadcrumb
          items={getBreadcrumbItems()}
          style={{ marginLeft: "1%" }}
          styles={BreadcrumbStyle}
        />
      )}
      <Stack horizontal tokens={{ childrenGap: 50 }}>
        <Card>
          <h1
            style={{
              fontSize: FontSizes.size20,
              display: "inline",
              fontWeight: FontWeights.bold,
              paddingLeft: "20px",
            }}
            role="heading"
            title="access-request"
            aria-label="user role request"
            aria-labelledby="user role request"
          >
            {`User Role Request`}
          </h1>
        </Card>
      </Stack>

      <Stack horizontal style={{
        paddingLeft: isUserSignedIn && !isUserAuthorized ? 0 : 70,
        width: 740,
        paddingTop: 10,
        paddingBottom: 15,
      }}>
        <Card>
          <Stack
            horizontal
            horizontalAlign="space-between"
            tokens={{ childrenGap: 20 }}
          >
            <Stack.Item>
              <ComboBox
                data-testid="user-select"
                styles={comboBoxStyles}
                label="User Role"
                placeholder="Select a user role"
                required
                autoComplete="on"
                options={UserCategories}
                selectedKey={selectedUser ? selectedUser.key : undefined}
                onChange={onUserChange}
                allowFreeInput
              />
            </Stack.Item>
            {selectedUser && (
              <Stack.Item>
                <ComboBox
                  data-testid="role-regions-select"
                  styles={comboBoxStyles}
                  label="Region"
                  ariaLabel="Role Regions Select"
                  required
                  autoComplete="on"
                  options={rolesData}
                  selectedKey={selectedRole ? selectedRole.key : undefined}
                  onChange={onRoleChange}
                  allowFreeInput
                />
              </Stack.Item>
            )}
          </Stack>
          <Stack style={{ padding: "10px 0px" }}>
            <p>
              Please allow 24-48 hours for your request to be reviewed and
              actioned upon. You will receive an email confirmation upon
              approval of your access request.
            </p>
          </Stack>
          <Stack
            style={{
              display: "flex",
              alignContent: "center",
              alignItems: "center",
            }}
          >
            <PrimaryButton
              text="Submit"
              type="button"
              disabled={!selectedRole}
              onClick={(e) => {
                e.preventDefault();
                console.log("Selected Role:", selectedRole);
                console.log(selectedRole?.requestUrl);
                if (selectedRole && selectedRole.requestUrl) {
                  window.open(selectedRole.requestUrl, "_blank");
                }
              }}
              style={{
                width: "100px",
                marginTop: "20px",
                padding: "10px",
              }}
            />
          </Stack>
        </Card>
      </Stack>
    </Stack>
  );
};
